import React, {useEffect, useMemo, useRef, useState} from "react";
import {Button, Form, InputGroup} from "react-bootstrap";
import {FaClock, FaSearch} from "react-icons/fa";
import {fetchAssignmentBookSearchSuggestions} from "../../http/bookAPI";

const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const BLOCKED_TERMS = [
    "anus", "anal", "asshole", "bitch", "bdsm", "cock", "cunt", "dick", "dildo",
    "fack", "fak", "fuck", "fucking", "nude", "porn", "porno", "pussy", "sex",
    "sexy", "shit", "slut", "whore",
];

const cleanSearch = (value) => String(value || "").replace(/\s+/g, " ").trim().slice(0, 120);

const getStorageKey = (type) => `virtalibry_assignment_${String(type || "book").toLowerCase()}_recent_searches`;

const formatSearch = (value) => {
    const clean = cleanSearch(value).toLowerCase();
    if (!clean) return "";
    return clean.charAt(0).toUpperCase() + clean.slice(1);
};

const isBlocked = (value) => {
    const clean = cleanSearch(value).toLowerCase();
    return BLOCKED_TERMS.some((term) => new RegExp(`(^|[^a-z0-9])${term}([^a-z0-9]|$)`, "i").test(clean));
};

const readRecent = (storageKey) => {
    try {
        const now = Date.now();
        const parsed = JSON.parse(localStorage.getItem(storageKey) || "[]");
        return parsed
            .filter((item) => item?.text && now - Number(item.ts || 0) < MAX_AGE_MS)
            .map((item) => formatSearch(item.text))
            .filter((text) => text && !isBlocked(text))
            .slice(0, 10);
    } catch (_) {
        return [];
    }
};

const saveRecent = (value, storageKey) => {
    const text = formatSearch(value);
    if (text.length < 2 || isBlocked(text)) return;
    try {
        const existing = readRecent(storageKey).filter((item) => item.toLowerCase() !== text.toLowerCase());
        localStorage.setItem(
            storageKey,
            JSON.stringify([{text, ts: Date.now()}, ...existing.map((item) => ({text: item, ts: Date.now()}))].slice(0, 10))
        );
    } catch (_) {}
};

const highlightMatch = (text, query) => {
    const value = String(text || "");
    const q = cleanSearch(query);
    if (!q) return value;
    const index = value.toLowerCase().indexOf(q.toLowerCase());
    if (index === -1) return value;
    return (
        <>
            {value.slice(0, index)}
            <span className="fw-semibold">{value.slice(index, index + q.length)}</span>
            {value.slice(index + q.length)}
        </>
    );
};

const AssignmentBookSearch = ({value, onChange, onSearch, type = "book"}) => {
    const [focused, setFocused] = useState(false);
    const [recent, setRecent] = useState([]);
    const [suggestions, setSuggestions] = useState([]);
    const wrapRef = useRef(null);
    const cleanValue = cleanSearch(value);
    const storageKey = useMemo(() => getStorageKey(type), [type]);

    useEffect(() => {
        setRecent(readRecent(storageKey));
    }, [focused, storageKey]);

    useEffect(() => {
        let alive = true;
        const q = cleanSearch(value);

        if (q.length < 2 || isBlocked(q)) {
            setSuggestions([]);
            return;
        }

        const timer = setTimeout(() => {
            fetchAssignmentBookSearchSuggestions(q, type)
                .then((data) => {
                    if (!alive) return;
                    const items = (data?.items || [])
                        .map(formatSearch)
                        .filter((item) => item && !isBlocked(item));
                    setSuggestions(items.slice(0, 10));
                })
                .catch(() => {
                    if (alive) setSuggestions([]);
                });
        }, 180);

        return () => {
            alive = false;
            clearTimeout(timer);
        };
    }, [value, type]);

    useEffect(() => {
        const close = (event) => {
            if (!wrapRef.current?.contains(event.target)) setFocused(false);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const visibleSuggestions = useMemo(() => {
        const rows = [];
        const seen = new Set();

        recent.forEach((text) => {
            if (!text || seen.has(text.toLowerCase())) return;
            if (cleanValue.length >= 2 && !text.toLowerCase().includes(cleanValue.toLowerCase())) return;
            seen.add(text.toLowerCase());
            rows.push({text, source: "local"});
        });

        if (cleanValue.length >= 2) {
            suggestions.forEach((text) => {
                if (!text || seen.has(text.toLowerCase())) return;
                seen.add(text.toLowerCase());
                rows.push({text, source: "global"});
            });
        }

        return rows.slice(0, 10);
    }, [recent, suggestions, cleanValue]);

    const submit = (nextValue = value) => {
        if (!cleanSearch(nextValue)) {
            setFocused(false);
            onChange("");
            onSearch("");
            return;
        }

        const text = formatSearch(nextValue);
        if (text.length < 2 || isBlocked(text)) return;
        saveRecent(text, storageKey);
        setRecent(readRecent(storageKey));
        setFocused(false);
        onChange(text);
        onSearch(text);
    };

    return (
        <div className="position-relative mb-2" ref={wrapRef}>
            <InputGroup size="sm">
                <Form.Control
                    value={value}
                    placeholder={type === "product" ? "Search products" : "Search books"}
                    className="border-secondary"
                    onChange={(event) => onChange(event.target.value)}
                    onFocus={() => setFocused(true)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") {
                            event.preventDefault();
                            submit();
                        }
                        if (event.key === "Escape") setFocused(false);
                    }}
                />
                <Button
                    variant="success"
                    className="border-secondary"
                    onClick={() => submit()}
                    aria-label={type === "product" ? "Search products" : "Search books"}
                >
                    <FaSearch />
                </Button>
            </InputGroup>

            {focused && visibleSuggestions.length > 0 && (
                <div
                    className="position-absolute bg-white border border-secondary rounded shadow-sm w-100 mt-1 p-1"
                    style={{zIndex: 20}}
                >
                    {visibleSuggestions.map((item) => (
                        <button
                            key={`${item.source}-${item.text}`}
                            type="button"
                            className="btn btn-light btn-sm w-100 text-start d-flex align-items-center gap-2"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                submit(item.text);
                            }}
                        >
                            {item.source === "local" ? <FaClock className="text-success flex-shrink-0" size={12} /> : <FaSearch className="text-success flex-shrink-0" size={12} />}
                            <span className="text-truncate">{highlightMatch(item.text, value)}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default AssignmentBookSearch;
